/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import classNames from 'classnames';
import type React from 'react';

import { ExclamationCircleIconSolid } from '../../icons/generated/heroIcons/ExclamationCircleIconSolid';
import { Typography } from '../../typography';
import {
  type FormFieldMessageSeverity,
  type FormFieldSize,
  type FormFieldState,
} from './form-control-types';

type FormFieldMessagePresentation = {
  /** Icon rendered before the message text. */
  icon?: React.ComponentType;
  /** Severity modifier class on the message container. */
  className?: string;
};

/**
 * How each severity presents itself. A future severity (`info`, `warning`) or
 * a new way of presenting a message extends this map rather than reshaping
 * the components that render messages.
 */
const presentationBySeverity: Record<
  FormFieldMessageSeverity,
  FormFieldMessagePresentation
> = {
  error: { className: 'ndl-error', icon: ExclamationCircleIconSolid },
  help: {},
};

type FormFieldMessageProps = {
  field: FormFieldState;
  size: FormFieldSize;
  /** Extra class names for the message container */
  className?: string;
  /** Wraps the message content, so a component can put it inside a Skeleton. */
  wrap?: (content: React.ReactNode) => React.ReactNode;
};

/**
 * The message below a form field: the help text, or the error that replaces
 * it. The container is rendered whether or not there is a message, so the
 * `aria-live` region exists in the document before any text arrives and a
 * validation error is reliably announced. The stylesheet collapses it while
 * it is empty.
 *
 * The region is polite rather than an assertive `role="alert"`, since
 * validation that runs on blur or on change would otherwise interrupt
 * whatever is being read. `aria-atomic` makes the whole message announce as
 * one, matching `role="status"` without presenting plain help text as a
 * status message.
 */
export const FormFieldMessage = ({
  field,
  size,
  className,
  wrap,
}: FormFieldMessageProps) => {
  const { message, messageId, messageSeverity } = field;
  const variant = size === 'large' ? 'body-medium' : 'body-small';
  const presentation =
    messageSeverity === undefined
      ? undefined
      : presentationBySeverity[messageSeverity];
  const Icon = presentation?.icon;

  const content =
    messageSeverity === undefined ? null : (
      // The icon and the text share a row of their own, so the container stays
      // a plain block that anything wrapping the content, a Skeleton in
      // particular, can fill the width of.
      <div className="ndl-form-message-content">
        {Icon !== undefined && (
          <div className="ndl-form-message-icon" aria-hidden="true">
            <Icon />
          </div>
        )}
        <Typography
          variant={variant}
          className="ndl-form-message-text"
          htmlAttributes={{ id: messageId }}
        >
          {message}
        </Typography>
      </div>
    );

  return (
    <div
      className={classNames(
        'ndl-form-message',
        {
          'ndl-large': size === 'large',
          'ndl-medium': size === 'medium',
          'ndl-small': size === 'small',
        },
        presentation?.className,
        className,
      )}
      aria-live="polite"
      aria-atomic="true"
    >
      {content !== null && (wrap === undefined ? content : wrap(content))}
    </div>
  );
};
